import { useMemo } from 'react';
import { useITopFiveApiV2EndpointsTopFiveGetTopFiveGetTopFiveEndpoint } from '@/api/api.generated';
import { ITopFiveApiV2EndpointsTopFiveGetTopFiveSearchType } from '@/api/models';
import { QUERY_CONFIG } from '@/api/queryConstants';
import {
  FilterType,
  FILTER_TO_SEARCH_TYPE_MAP,
} from '../constants/topFiveOptions';

type UseTopFiveDataParams = {
  searchText?: string;
  searchType?: FilterType;
  enabled?: boolean;
};

export const useTopFiveData = (params?: UseTopFiveDataParams) => {
  const { searchText, searchType, enabled = true } = params || {};

  // Only compute API parameters when enabled and search is valid
  const shouldUseSearchParams =
    enabled && searchText && searchText.trim().length >= 3;

  // Map FilterType to API SearchType
  const apiSearchType = useMemo(() => {
    if (!shouldUseSearchParams) {
      return null; // No search type when not searching
    }
    return searchType
      ? FILTER_TO_SEARCH_TYPE_MAP[searchType] ??
          ITopFiveApiV2EndpointsTopFiveGetTopFiveSearchType.Name
      : ITopFiveApiV2EndpointsTopFiveGetTopFiveSearchType.Name;
  }, [searchType, shouldUseSearchParams]);

  // Use searchText or null if empty/too short
  const apiSearchText = useMemo(() => {
    return shouldUseSearchParams ? searchText.trim() : null;
  }, [searchText, shouldUseSearchParams]);

  const result = useITopFiveApiV2EndpointsTopFiveGetTopFiveGetTopFiveEndpoint(
    {
      searchText: apiSearchText,
      searchType: apiSearchType,
    },
    {
      query: {
        enabled,
        staleTime: QUERY_CONFIG.STALE_TIME.MEDIUM,
        gcTime: QUERY_CONFIG.GC_TIME.MEDIUM,
        refetchOnWindowFocus: false,
        retry: QUERY_CONFIG.RETRY.MEDIUM,
      },
    }
  );

  const { data, isLoading, error } = result;

  const topFiveData = useMemo(
    () => ({
      currentPeriod: data?.data?.currentPeriod || [],
      previousPeriod: data?.data?.previousPeriod || [],
      hasAddTopFiveAccess: data?.data?.hasAddTopFiveAccess || false,
    }),
    [data]
  );

  return {
    topFiveData,
    isLoading,
    error,
    refetch: result.refetch,
  };
};
